// src/prototype/pages/EAPrintableCFOSummaryPage.tsx
// ============================================================
// DVA EA DELIVERABLE — CFO ROI SUMMARY
// CI pass March 2026: financial formatting, value-driver
// specificity, scenario clarity, recommendation strength.
// No structural or architectural changes.
// ============================================================

import React from 'react';
import { DEMO_CLIENT } from '../config/demoClient';
import { demoMonthYear } from '../config/demoDates';
import {
  EAUseCaseArtifactShell,
  UACallout,
  UAMetricRow,
  type SummaryMetric,
  type ArtifactSection,
} from '../components/ea_reports/use_case/EAUseCaseArtifactShell';
import {
  CANON,
  CANON_CONFIDENCE_BASIS,
  CARE_COORD_DISCLOSURE,
  CARE_COORD_ROI,
  CARE_COORD_VALUE_CHAIN as VALUE_CHAIN_SENTENCE,
} from '../roi/roiCanon';
import { TOTAL_PORTFOLIO_SIZE } from '../data/decisionBrief';
import {
  bandHighOf,
  bandLowOf,
  formatDriverValue,
  formatMonths,
  formatUsd,
  formatUsdRange,
  isScenarioVaried,
} from '../roi/roiEngine';

// ─────────────────────────────────────────────────────────────────────────────
// EVERY FIGURE IN THIS DOCUMENT COMES FROM THE ROI ENGINE
//
// It previously carried nine independently typed financial literals, including
// "$2.1M – $3.4M required investment" (which contradicted the Execute Command
// Center's "$1.2M"), an "85%" model confidence with no rule behind it, and a
// "First-Year NPV $11.6M – $16.4M" that appeared nowhere else in the codebase.
//
// Investment is now stated as the resolved canon: implementation + operating,
// with the 3-year total labelled as a TOTAL rather than as an implementation
// cost. See src/prototype/roi/roiCanon.ts.
// ─────────────────────────────────────────────────────────────────────────────

const SCENARIOS = CARE_COORD_ROI.scenarios;

// ─────────────────────────────────────────────────────────────────────────────
// SUMMARY STRIP METRICS
// ─────────────────────────────────────────────────────────────────────────────

const SUMMARY_METRICS: SummaryMetric[] = [
  {
    label: 'Required Investment',
    value: CANON.totalCost3Yr,
    sub: 'total 3-year cost',
  },
  {
    label: 'Estimated Annual Value',
    value: CANON.annualValueRange,
    accent: true,
    sub: 'conservative–aggressive span',
  },
  {
    label: 'Payback Period',
    value: CANON.basePayback,
    accent: true,
    sub: 'base case',
  },
  {
    label: 'Model Confidence',
    value: CANON.confidencePct,
    sub: 'sensitivity-weighted inputs',
  },
  {
    label: 'Implementation Complexity',
    value: 'Low',
    sub: 'relative to portfolio',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// SCENARIO TABLE
// ─────────────────────────────────────────────────────────────────────────────

const SCENARIO_ROWS = [
  { scenario: 'Conservative', out: SCENARIOS.conservative, accent: false },
  { scenario: 'Base Case', out: SCENARIOS.base, accent: true },
  { scenario: 'Aggressive', out: SCENARIOS.aggressive, accent: false },
].map(({ scenario, out, accent }) => ({
  scenario,
  accent,
  value: formatUsdRange(out.annualValueLow, out.annualValueHigh),
  payback: formatMonths(out.paybackMonths),
  net: formatUsd(out.netValue3Yr),
}));

const ScenarioTable: React.FC = () => (
  <div style={{ marginTop: '8px' }}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0', marginBottom: '4px' }}>
      {['Scenario', 'Annual Value', 'Payback', 'Net 3-Yr Value'].map((h) => (
        <div key={h} style={{ fontSize: '6.5pt', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#717182', padding: '6px 12px', background: '#F5F7FA', borderBottom: '2px solid #18365E' }}>
          {h}
        </div>
      ))}
    </div>
    {SCENARIO_ROWS.map((row, i) => (
      <div key={row.scenario} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0', background: row.accent ? 'rgba(24,54,94,0.04)' : i % 2 === 0 ? '#ffffff' : '#fafafa', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ padding: '10px 12px', fontSize: '9pt', fontWeight: row.accent ? 700 : 500, color: '#18365E', borderRight: '1px solid #e5e7eb' }}>
          {row.accent && (
            <span style={{ display: 'inline-block', fontSize: '6pt', fontWeight: 700, color: '#FF6E4C', background: 'rgba(255,110,76,0.1)', border: '1px solid rgba(255,110,76,0.3)', borderRadius: '10px', padding: '1px 6px', marginRight: '6px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Recommended
            </span>
          )}
          {row.scenario}
        </div>
        <div style={{ padding: '10px 12px', fontSize: '9pt', fontWeight: row.accent ? 700 : 400, color: row.accent ? '#FF6E4C' : '#374151', borderRight: '1px solid #e5e7eb' }}>
          {row.value}
        </div>
        <div style={{ padding: '10px 12px', fontSize: '9pt', fontWeight: row.accent ? 700 : 400, color: '#374151', borderRight: '1px solid #e5e7eb' }}>
          {row.payback}
        </div>
        <div style={{ padding: '10px 12px', fontSize: '9pt', fontWeight: row.accent ? 700 : 400, color: '#374151' }}>
          {row.net}
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// HOW THESE ARE COMPUTED — the same definitions the ROI Model screen discloses
// ─────────────────────────────────────────────────────────────────────────────

const MethodTable: React.FC = () => (
  <div style={{ marginTop: '8px' }}>
    {CARE_COORD_DISCLOSURE.map((line, i) => (
      <div key={line.term} style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', background: i % 2 === 0 ? '#F5F7FA' : '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ padding: '8px 12px', fontSize: '8pt', fontWeight: 700, color: '#18365E', borderRight: '1px solid #e5e7eb' }}>{line.term}</div>
        <div style={{ padding: '8px 12px', fontSize: '8pt', fontWeight: 400, color: '#374151', lineHeight: 1.5 }}>{line.definition}</div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ASSUMPTIONS TABLE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The model's own inputs. Built from the driver set so this table cannot state a
 * value the arithmetic above does not use — the previous version quoted "65% of
 * high-risk patients" for care management coverage, a figure no computation
 * consumed and which disagreed with the Assumptions Appendix's own 60%.
 */
const ASSUMPTION_ROWS = CARE_COORD_ROI.drivers.map((driver) => ({
  assumption: driver.name.replace(/\s*\([^)]*\)\s*$/, ''),
  value: isScenarioVaried(driver)
    ? `${formatDriverValue(driver, driver.base)} at base (${formatDriverValue(
        driver,
        driver.conservative,
      )} – ${formatDriverValue(driver, driver.aggressive)} across scenarios)`
    : `${formatDriverValue(driver, driver.base)} (tested ${formatDriverValue(
        driver,
        bandLowOf(driver),
      )} – ${formatDriverValue(driver, bandHighOf(driver))})`,
  basis: `${driver.source} · ${driver.sourceType} · ${driver.confidence} confidence`,
}));

const AssumptionsTable: React.FC = () => (
  <div style={{ marginTop: '8px' }}>
    {ASSUMPTION_ROWS.map((row, i) => (
      <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr', background: i % 2 === 0 ? '#F5F7FA' : '#ffffff', borderBottom: '1px solid #e5e7eb', borderRadius: i === 0 ? '6px 6px 0 0' : i === ASSUMPTION_ROWS.length - 1 ? '0 0 6px 6px' : '0' }}>
        <div style={{ padding: '9px 12px', fontSize: '8.5pt', fontWeight: 600, color: '#18365E', borderRight: '1px solid #e5e7eb' }}>{row.assumption}</div>
        <div style={{ padding: '9px 12px', fontSize: '8.5pt', fontWeight: 400, color: '#374151', borderRight: '1px solid #e5e7eb' }}>{row.value}</div>
        <div style={{ padding: '9px 12px', fontSize: '8pt', fontWeight: 400, color: '#717182', fontStyle: 'italic' }}>{row.basis}</div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// RECOMMENDATION BLOCK
// ─────────────────────────────────────────────────────────────────────────────

const RecommendationBlock: React.FC = () => (
  <div>
    <div style={{ background: '#18365E', borderRadius: '8px', padding: '18px 22px', marginBottom: '14px' }}>
      <div style={{ fontSize: '7pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF6E4C', marginBottom: '8px' }}>
        CFO Funding Recommendation
      </div>
      <p style={{ fontSize: '10pt', fontWeight: 700, color: '#ffffff', lineHeight: 1.55, margin: 0 }}>
        Funding approval is recommended for Care Coordination and Transition Management.
      </p>
      <p style={{ fontSize: '9pt', fontWeight: 400, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: '8px 0 0' }}>
        The financial model indicates a {CANON.basePayback} payback period at base case assumptions, and an
        estimated annual value of {CANON.annualValueRange} across the conservative–aggressive span against{' '}
        {CANON.investmentSchedule} — {CANON.totalCostLabel}.
        Model confidence is {CANON.confidencePct} ({CANON_CONFIDENCE_BASIS}).
        Leadership review and Phase 1 initiation are recommended as near-term next steps following governance approval.
        Governance submission materials are available in the Board Rationale.
        Detailed model inputs appear in the Assumptions Appendix.
      </p>
    </div>
    <UAMetricRow metrics={[
      { label: 'Total 3-Year Cost',   value: CANON.totalCost3Yr          },
      { label: 'Annual Value',        value: CANON.annualValueRange      },
      { label: 'Payback Period',      value: CANON.basePayback           },
      { label: 'Net 3-Year Value',    value: CANON.baseNetValue3Yr       },
    ]} />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SECTIONS
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS: ArtifactSection[] = [
  {
    title: 'Executive Financial Summary',
    content: (
      <>
        <p>
          Care Coordination and Transition Management is a Tier 1 Priority initiative projected to
          deliver {CANON.annualValueRange} in annual financial value across the conservative–aggressive
          span against {CANON.investmentSchedule} — {CANON.totalCostLabel}. At base case, payback is
          achieved {CANON.basePayback} after deployment begins, and net value over three years —
          benefits discounted at {CANON.discountRate}, less total cost — is {CANON.baseNetValue3Yr}.
          This is one of the fastest return profiles in the current initiative portfolio.
        </p>
        <UACallout>
          This initiative ranks #1 of {TOTAL_PORTFOLIO_SIZE} fully evaluated initiatives — 99th
          percentile — based on composite business value and implementation feasibility scoring.
        </UACallout>
        <p>
          Financial projections are derived from industry benchmarks, peer integrated delivery
          network performance data, and scenario modeling. Three scenarios (conservative, base,
          aggressive) reflect variation in adoption rate and intervention performance.
          Low implementation complexity reduces execution risk and supports an accelerated
          deployment timeline. All assumptions are structured for formal finance validation
          prior to funding commitment.
        </p>
      </>
    ),
  },
  {
    title: 'Primary Value Driver',
    content: (
      <>
        <p>
          Reduced readmissions and improved care coordination efficiency across high-risk transition
          populations represent the primary financial return for this initiative.
        </p>
        <p>
          Predictive risk stratification deployed at discharge enables care teams to intervene with
          the highest-risk patients before readmission occurs. The base-case chain is auditable in
          one line: {VALUE_CHAIN_SENTENCE}
        </p>
        <p>
          Secondary value is realized through reduced manual care coordination effort, elimination
          of duplicative follow-up contacts, and improved payer contract performance tied to
          readmission quality metrics.
        </p>
      </>
    ),
  },
  {
    title: 'ROI Scenario Analysis',
    pageBreakBefore: false,
    content: (
      <>
        <p>
          Three scenarios are modeled based on variation in adoption rate, readmission reduction
          magnitude, and care management coverage.
        </p>
        <ScenarioTable />
        <p style={{ marginTop: '12px', fontSize: '8.5pt', color: '#717182', fontStyle: 'italic' }}>
          The base case assumes moderate adoption of care coordination workflows and validated
          improvements in transition management outcomes. The conservative scenario reflects slower
          adoption, while the aggressive scenario assumes broader operational uptake and stronger
          intervention performance.
        </p>
      </>
    ),
  },
  {
    title: 'Payback Outlook',
    content: (
      <p>
        The {CANON.implementationCost} implementation spend is projected to recover in{' '}
        {SCENARIOS.aggressive.paybackMonths}–{SCENARIOS.conservative.paybackMonths} months depending on
        adoption speed, care management uptake, and operational readiness — {CANON.basePayback} at base
        case. Net value over three years, with benefits discounted at {CANON.discountRate} and net of the{' '}
        {CANON.totalCostLabel}, is {CANON.conservativeNetValue3Yr} at conservative assumptions and{' '}
        {CANON.baseNetValue3Yr} at base case — a strong return on a contained commitment.
      </p>
    ),
  },
  {
    title: 'How These Figures Are Computed',
    content: (
      <>
        <p>
          Every figure in this document is produced by one financial model from the inputs listed in
          the Assumptions Appendix. The definitions below are that model's; they are identical to the
          ones disclosed on the ROI Model screen, and each figure above can be recomputed from them.
        </p>
        <MethodTable />
      </>
    ),
  },
  {
    title: 'Key Financial Assumptions',
    pageBreakBefore: true,
    content: (
      <>
        <p>
          The following assumptions underpin the financial model, reviewed against CMS benchmarks,
          peer network performance data, and internal operational estimates.
        </p>
        <AssumptionsTable />
        <p style={{ marginTop: '12px', fontSize: '8.5pt', color: '#717182', fontStyle: 'italic' }}>
          Assumptions are subject to validation during the discovery phase. A formal assumption
          review with Finance is recommended prior to board funding approval.
          Complete documentation of model inputs, benchmark sources, and confidence assessment
          appears in the Assumptions Appendix.
        </p>
      </>
    ),
  },
  {
    title: 'CFO Recommendation',
    content: <RecommendationBlock />,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function EAPrintableCFOSummaryPage() {
  return (
    <EAUseCaseArtifactShell
      title="CFO ROI Summary"
      subtitle="Finance Investment Brief"
      clientName={DEMO_CLIENT.name}
      useCaseName="Care Coordination and Transition Management"
      reportDate={demoMonthYear()}
      summaryMetrics={SUMMARY_METRICS}
      sections={SECTIONS}
      manualPrint
    />
  );
}